import { useState } from "react";

export default function Research() {
  const [email, setEmail] = useState("");

  return (
    <>
      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[614px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-data-teal/90 to-primary/40 z-10"></div>
        <img
          className="absolute inset-0 w-full h-full object-cover"
          alt="Agricultural research facility"
          src="https://lh3.googleusercontent.com/aida-public/AB6AXuAB3Nd7pAduadMZPM3by13xKm48EsEIO0sawmyrWL8j7Izc1kEfr4NmklPHLVV_gGfBihkw4CAOtJJWKavC0vQGtrv1DTafiQLrM1Tz29JpPVwIYBiv0fXBdsxlrSZjRr9G_GJkFLw-1acQ2WHrCIdAEALhcgV-oHtx_VzFekE2m5OM-V9HId836hZkn583ihQK80dJhN-H9tXht7iu6fvNUxZUVGv4Qw_uf7u0w6M_XDbcHmS25cGYRrsYIEWgI5QfZ4bsI-xr_bM"
        />
        <div className="relative z-20 text-center px-margin-mobile md:px-0">
          <h1 className="font-headline-xl text-headline-xl text-white mb-4">Advancing Seed Science</h1>
          <p className="text-secondary-fixed max-w-2xl mx-auto font-body-lg text-body-lg">Precision-driven research at the intersection of biological growth and artificial intelligence.</p>
        </div>
      </section>

      {/* Bento Research Grid */}
      <section className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter">
          {/* Main Research Overview */}
          <div className="col-span-1 md:col-span-12 lg:col-span-8 bg-white p-8 md:p-12 rounded-xl shadow-sm border-l-8 border-growth-green">
            <span className="font-label-mono text-label-mono text-primary mb-2 block">CORE INITIATIVE</span>
            <h2 className="font-headline-lg text-headline-lg mb-6">Cucumber Seed Quality & Vigor</h2>
            <p className="font-body-lg text-body-lg text-on-surface-variant mb-8">
              Our research focuses on non-destructive assessment techniques to predict germination rates and seedling vigor. By analyzing the morphology and metabolic markers of cucumber seeds, we establish high-accuracy benchmarks for commercial nurseries.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: "magnification_small", label: "Morphological Analysis" },
                { icon: "monitoring", label: "Metabolic Tracking" },
                { icon: "precision_manufacturing", label: "Precision Sorting" },
              ].map((item) => (
                <div key={item.label} className="bg-surface-container-low p-4 rounded-lg">
                  <span className="material-symbols-outlined text-primary mb-2">{item.icon}</span>
                  <div className="font-label-bold text-label-bold uppercase">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Computer Vision Card */}
          <div className="col-span-1 md:col-span-12 lg:col-span-4 bg-data-teal text-white p-8 rounded-xl flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="material-symbols-outlined text-cucumber-blossom">visibility</span>
                <span className="font-label-mono text-label-mono">TECH STACK</span>
              </div>
              <h3 className="font-headline-md text-headline-md mb-4">Computer Vision & AI</h3>
              <p className="font-body-md text-body-md opacity-80">
                Implementing proprietary neural networks to monitor crop growth in real-time. Our AI models detect early stress markers before they are visible to the human eye.
              </p>
            </div>
            <div className="mt-8">
              <div className="flex justify-between items-end">
                <div className="text-2xl font-bold text-cucumber-blossom">High Accuracy</div>
                <div className="text-xs font-label-mono opacity-60">MODEL PERFORMANCE</div>
              </div>
              <div className="w-full h-1 bg-white/20 mt-2 rounded-full overflow-hidden">
                <div className="h-full bg-cucumber-blossom w-[95%]"></div>
              </div>
            </div>
          </div>

          {/* Scientific Protocols */}
          <div className="col-span-1 md:col-span-6 glass-card p-8 rounded-xl">
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <span className="material-symbols-outlined text-primary">verified</span>
              </div>
              <div>
                <h3 className="font-headline-md text-headline-md mb-2">Scientific Protocols</h3>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  All CAFE research methodologies are validated in collaboration with IIT Ropar and the AWaDH foundation. This ensures our agri-tech solutions meet rigorous international standards for scientific integrity and field reliability.
                </p>
              </div>
            </div>
          </div>

          {/* Open Data & Publications */}
          <div className="col-span-1 md:col-span-6 bg-seed-husk/30 border border-seed-husk p-8 rounded-xl">
            <h3 className="font-headline-md text-headline-md mb-4">Open Data & Publications</h3>
            <ul className="space-y-4">
              <li className="flex justify-between items-center group cursor-pointer">
                <div>
                  <p className="font-label-bold text-label-bold text-earth-clay">UPCOMING</p>
                  <p className="font-body-md text-body-md font-bold">Spectral Imaging for Seed Vigor Prediction</p>
                </div>
                <span className="material-symbols-outlined text-earth-clay group-hover:translate-x-1 transition-transform">arrow_forward</span>
              </li>
              <li className="flex justify-between items-center group cursor-pointer border-t border-seed-husk pt-4">
                <div>
                  <p className="font-label-bold text-label-bold text-earth-clay">IN REVIEW</p>
                  <p className="font-body-md text-body-md font-bold">AI-Driven Phenotyping in Tropical Climates</p>
                </div>
                <span className="material-symbols-outlined text-earth-clay group-hover:translate-x-1 transition-transform">arrow_forward</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Technology Deep Dive */}
      <section className="bg-surface-container py-24">
        <div className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-cucumber-blossom/20 rounded-full blur-2xl"></div>
            <img
              className="rounded-xl shadow-lg relative z-10 w-full aspect-video object-cover"
              alt="Crop vigor monitoring"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuBwH-kMN7D3cxK3JvIVisjUKKYMnaeWq6Vj7brF_q2TS6DwQw0l2qE2zrILrekj47BHjt8pEEtsaqKjSTj_kM0AYm7W2sGYvFo0RfNSfhSaDQrtHPelYcjBAEHUneB4IaxMwumEoeZYy5eMzJ5hI6Pc0mcS2w-TUOILU3fFwciM3Sy-ecDDNAAxphF0ALxSXccUr9ExPzg0vyhsSGEW4nEbDJPmACp8m4pt5EWtS6ZlEjMgTsieMT7pPKD3N6JwkO-ND_icLC40B0Y"
            />
            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-lg shadow-xl z-20 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-growth-green animate-pulse"></div>
                <span className="font-label-mono text-label-mono">SYSTEM ACTIVE: LIVE VIGOR FEED</span>
              </div>
            </div>
          </div>
          <div>
            <h2 className="font-headline-lg text-headline-lg mb-6">Real-time Crop Vigor Monitoring</h2>
            <p className="font-body-lg text-body-lg text-on-surface-variant mb-8">
              Our Computer Vision system utilizes multi-spectral cameras to capture biological data points. By processing these through our custom-trained AI, we can identify "Growth Lag" in seed batches before physiological symptoms appear.
            </p>
            <div className="space-y-4">
              {[
                "Automated image acquisition at regular intervals.",
                "Cloud-based neural processing for stress detection.",
                "Actionable insights sent directly to nursery dashboards.",
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-4">
                  <span className="bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">✓</span>
                  <span className="font-body-md text-body-md">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AWaDH Partnership */}
      <section className="py-16 px-margin-mobile md:px-margin-desktop bg-surface-container-low">
        <div className="max-w-container-max mx-auto flex flex-col md:flex-row items-center justify-center gap-8">
          <p className="font-headline-md text-headline-md text-data-teal">Research powered by</p>
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <img src="/IIT-Ropar-AWaDH.webp" alt="iHub-AWaDH IIT Ropar" className="h-16 object-contain" />
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-24 text-center">
        <div className="bg-data-teal rounded-3xl p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="font-headline-lg text-headline-lg text-white mb-4">Access our Research Database</h2>
            <p className="text-secondary-fixed mb-8">Join our network of scientists and seed producers to receive early access to our open-source datasets and upcoming publications.</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <input
                className="px-6 py-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-cucumber-blossom transition-colors md:w-80"
                placeholder="professional@institution.edu"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button className="bg-cucumber-blossom text-data-teal font-label-bold text-label-bold px-8 py-4 rounded-lg hover:bg-white transition-colors">Request Access</button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
